import { MapContainer, TileLayer, Polygon, useMap } from "react-leaflet";
import {
  LatLngBoundsExpression,
  LatLngExpression,
  LatLngLiteral,
  LatLngTuple,
} from "leaflet";
import { useEffect, useMemo } from "react";
import { ConfigKey } from "enum/ConfigKey";
import { getConfig } from "config/app.config";
import { Box, Button } from "@chakra-ui/react";
import { Star } from "lucide-react";
import { SaveGeofenceModal } from "../../../Alert/components/SaveGeofenceModal";
import { useSaveGeofenceModal } from "../../../Alert/hooks/useSaveGeofenceModal";

function FitPolygonBounds({
  positions,
}: {
  positions: LatLngExpression[][] | LatLngExpression[] | number[][];
}) {
  const map = useMap();

  //TODO: Backend needs to return the lat lng object in the correct way
  const isLatLngArray = (
    coords: LatLngExpression[][] | number[][]
  ): coords is LatLngExpression[][] =>
    coords.length > 0 &&
    coords[0].length > 0 &&
    typeof coords[0][0] === "object" &&
    coords[0][0] !== null &&
    "lat" in coords[0][0] &&
    "lng" in coords[0][0];

  useEffect(() => {
    if (positions.length > 0) {
      const formattedPositions = isLatLngArray(
        positions as LatLngExpression[][]
      )
        ? positions.flat()
        : positions;

      const bounds: LatLngBoundsExpression = formattedPositions.map((pos) => {
        if (Array.isArray(pos)) {
          return [pos[0], pos[1]] as LatLngTuple;
        } else if (typeof pos === "object" && "lat" in pos && "lng" in pos) {
          return [pos.lat, pos.lng] as LatLngTuple;
        }
        throw new Error("Invalid format in positions");
      });

      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [positions, map]);

  return null;
}

export function MapGeofence({
  selectedGeofence,
  handleDrawingMode,
  clearGeofence,
  t,
  hideEditButtons = false,
  hideSaveAreaButton = false,
  isGeofenceInvalid = false,
  setFieldValue,
}: {
  selectedGeofence: LatLngLiteral[][] | LatLngLiteral[];
  handleDrawingMode?: () => void;
  clearGeofence?: () => void;
  t: (key: string) => string;
  hideEditButtons?: boolean;
  hideSaveAreaButton?: boolean;
  isGeofenceInvalid?: boolean;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
}) {
  const mapTilerKey = getConfig(ConfigKey.REACT_APP_MAPTILER_KEY);

  const {
    isModalOpen,
    isSaving,
    isSaved,
    handleOpenModal,
    handleCloseModal,
    handleSubmit,
    normalizedGeofence,
  } = useSaveGeofenceModal(selectedGeofence, setFieldValue);

  const saveAreaIcon = useMemo(() => {
    const style = { marginBottom: "4px" };
    return isSaved ? (
      <Star size={16} fill="currentColor" style={style} />
    ) : (
      <Star size={16} style={style} />
    );
  }, [isSaved]);

  return (
    <Box position="relative" width="100%" height="256px">
      <MapContainer
        center={[0, 0]}
        zoom={8}
        style={{
          height: "256px",
          width: "100%",
          borderRadius: "8px",
          overflow: "hidden",
        }}
        scrollWheelZoom={false}
        dragging={false}
        doubleClickZoom={false}
        zoomControl={false}
        touchZoom={false}
        boxZoom={false}
        keyboard={false}
      >
        <TileLayer
          attribution={
            '\u003ca href="https://www.maptiler.com/copyright/" target="_blank"\u003e\u0026copy; MapTiler\u003c/a\u003e \u003ca href="https://www.openstreetmap.org/copyright" target="_blank"\u003e\u0026copy; OpenStreetMap contributors\u003c/a\u003e'
          }
          tileSize={512}
          zoomOffset={-1}
          minZoom={1}
          crossOrigin={true}
          url={`https://api.maptiler.com/maps/019b6b50-0ba6-7f22-825f-03de06fca8a8/{z}/{x}/{y}.webp?key=${mapTilerKey}`}
          zIndex={700}
        />

        <Polygon
          positions={selectedGeofence}
          pathOptions={{ color: "#309E3A" }}
          interactive={false}
        />

        <FitPolygonBounds positions={selectedGeofence} />
      </MapContainer>

      {!hideEditButtons && !isSaved && (
        <>
          <Button
            layerStyle="newDarkOcean"
            _hover={{ layerStyle: "newDarkOcean" }}
            position="absolute"
            top="10px"
            right="10px"
            zIndex={1000}
            size="sm"
            onClick={handleDrawingMode}
            w="150px"
            fontSize={"16px"}
          >
            {t("edit")}
          </Button>

          <Button
            layerStyle="newDarkOcean"
            _hover={{ layerStyle: "newDarkOcean" }}
            position="absolute"
            top="50px"
            right="10px"
            zIndex={1000}
            size="sm"
            onClick={clearGeofence}
            w="150px"
            fontSize={"16px"}
          >
            {t("clear")}
          </Button>
        </>
      )}

      {!hideSaveAreaButton && (
        <Button
          layerStyle="newDarkOcean"
          _hover={{ layerStyle: "newDarkOcean" }}
          position="absolute"
          bottom="20px"
          right="10px"
          zIndex={1000}
          size="sm"
          onClick={handleOpenModal}
          w="150px"
          fontSize={"16px"}
          cursor={isSaved || isGeofenceInvalid ? "not-allowed" : undefined}
          leftIcon={saveAreaIcon}
          disabled={isGeofenceInvalid}
          pointerEvents={isSaved || isGeofenceInvalid ? "none" : "auto"}
        >
          {isSaved ? t("areaSaved") : t("saveArea")}
        </Button>
      )}

      <SaveGeofenceModal
        isOpen={isModalOpen}
        geofence={normalizedGeofence}
        handleSubmit={handleSubmit}
        handleClose={handleCloseModal}
        isLoading={isSaving}
      />
    </Box>
  );
}
